import type { RouteHandler } from './types';
const routes: Array<{ rx: RegExp; keys: string[]; handler: RouteHandler; pattern: string }> = [];
let notFound: RouteHandler;
const listeners = new Set<() => void>();
export function route(pattern: string, handler: RouteHandler) {
  const keys: string[] = [];
  const rx = new RegExp('^' + pattern.replace(/\/:([A-Za-z_]+)/g, (_, key) => { keys.push(key); return '/([^/]+)'; }) + '/?$');
  routes.push({ rx, keys, handler, pattern });
}
export function fallback(handler: RouteHandler) { notFound = handler; }
export function parseHash(hash = location.hash) {
  const raw = hash.replace(/^#/, '') || '/';
  const index = raw.indexOf('?');
  return { path: index < 0 ? raw : raw.slice(0, index), query: Object.fromEntries(new URLSearchParams(index < 0 ? '' : raw.slice(index + 1))) };
}
export function resolve(hash = location.hash) {
  const { path, query } = parseHash(hash);
  for (const item of routes) {
    const match = path.match(item.rx);
    if (!match) continue;
    const params: Record<string, string> = {};
    item.keys.forEach((key, index) => { params[key] = decodeURIComponent(match[index + 1]); });
    return { handler: item.handler, params, query, path, pattern: item.pattern };
  }
  return { handler: notFound, params: {}, query, path, pattern: null };
}
export function href(path: string, query?: Record<string, string | number | null | undefined>) {
  const qs = new URLSearchParams(Object.entries(query || {}).filter(([, value]) => value != null && value !== '').map(([key, value]) => [key, String(value)]));
  return '#' + path + (qs.size ? '?' + qs : '');
}
export function go(path: string, query?: Record<string, string>) { location.hash = href(path, query).slice(1); }
export function setQuery(patch: Record<string, string | null>, { replace = true } = {}) {
  const { path, query } = parseHash();
  history[replace ? 'replaceState' : 'pushState'](null, '', href(path, { ...query, ...patch }));
}
export function onChange(callback: () => void) { listeners.add(callback); return () => { listeners.delete(callback); }; }
const returnPages = new Map<string, string>();
let returning = false;
export function rememberNavigation(from: string, to: string) {
  if (!/^#\/(?!\/)/.test(from) || !/^#\/(?!\/)/.test(to)) return;
  const source = parseHash(from).path, destination = parseHash(to).path;
  if (source !== destination) returnPages.set(destination, from);
}
export function returnHref(fallback: string, current = location.hash) {
  return returnPages.get(parseHash(current).path) || fallback;
}
export function goBack(fallback: string) {
  const target = returnHref(fallback);
  returnPages.delete(parseHash().path);
  returning = true;
  location.hash = target.slice(1);
}
export function start() {
  // Hash navigation emits both popstate and hashchange; track it only once.
  const fire = (event: HashChangeEvent) => {
    const from = new URL(event.oldURL).hash || '#/';
    const current = location.hash || '#/';
    if (returning) returning = false;
    else rememberNavigation(from, current);
    listeners.forEach(callback => callback());
  };
  window.addEventListener('hashchange', fire);
  return () => { window.removeEventListener('hashchange', fire); };
}
